import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Lightbulb } from 'lucide-react'
import { Card, Button, Badge, Select, Toggle } from '@/components/ui'
import { Alert } from '@/components/ui/Alert'
import { Input } from '@/components/ui/Input'
import { PricingFormulaEditor } from '@/components/ui'
import { PricingPreview } from '@/components/ui/PricingPreview'
import { ExplainerCard } from '@/components/ui/ExplainerCard'
import { useUpdateProperty } from '@/hooks/useProperties'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { Property, PricingFormula, PricingItem, PaymentMethodConfig, LateFeePolicy } from '@/types'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PropertyPricingFieldsProps {
  /** Pricing settings only apply to existing properties — property is always required. */
  property: Property
  /** Called with the saved Property after a successful PATCH. */
  onSave: (saved: Property) => void
  /**
   * Optional ref that the wizard wires to trigger a save externally.
   * When provided, the ref.current is set to the component's handleSave function
   * so the wizard's "Save & Continue" button can call ref.current() directly.
   */
  saveRef?: React.RefObject<(() => Promise<void>) | null>
  /**
   * Optional callback fired whenever this component's dirty state changes.
   * Used by PropertyDetailPage to aggregate per-tab dirty state for the
   * global Save button and dirty-dot badges.
   *
   * Dirty is true if any of the uncontrolled useState fields differ from the
   * property prop's values (pricingFormula, overridePayment, paymentMethods,
   * lateFeePolicy).
   */
  onDirtyChange?: (isDirty: boolean) => void
  /**
   * Optional banner rendered above the pricing formula editor (EP-51).
   * Used by OnboardingWizardPage to show the ApplyFromFileButton when
   * parsed_data.pricing_formula is available and not yet applied.
   */
  prefillBanner?: React.ReactNode
  /**
   * When provided AND the current pricingFormula state is empty, immediately
   * loads these items into the editor (EP-51 Apply-from-file path).
   * Does NOT overwrite if the user has already made edits.
   */
  prefillPricingFormula?: PricingItem[]
  /**
   * When true (wizard Step 2 / onboarding), renders only the pricing formula
   * and live bill preview inline; payment methods and common-area fees are
   * placed inside a collapsed OptionalSection ("Advanced options") so they
   * remain accessible without cluttering the wizard step. Default `false`.
   *
   * NOT piggybacked on `saveRef` — these are independent concerns (external
   * save trigger vs. streamlined layout). S2-1.
   */
  streamlinedMode?: boolean
  /**
   * Optional callback fired whenever the pricing formula state changes.
   * Used by ImportDetailPage (T021) to track edits in draft mode (before a
   * property is created). When provided the parent gets every intermediate
   * formula state so it can persist the draft.
   *
   * Does NOT affect the save-to-property path — that still goes through
   * handleSave / saveRef as before. Additive; backward-compatible.
   */
  onFormulaChange?: (formula: PricingFormula) => void
  className?: string
}

// ---------------------------------------------------------------------------
// Default late fee policy used when the property has none stored yet
// ---------------------------------------------------------------------------

const DEFAULT_LATE_FEE_POLICY: LateFeePolicy = {
  enabled: false,
  type: 'fixed',
  amount: 0,
  grace_period_days: 0,
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Self-contained pricing settings form for an existing property.
 *
 * Owns its own state for:
 *   - pricingFormula   (PricingFormula array)
 *   - overridePayment  (boolean toggle)
 *   - paymentMethods   (PaymentMethodConfig)
 *   - lateFeePolicy    (LateFeePolicy)
 *
 * Saves everything in a single PATCH /api/properties/:id.
 *
 * Three sections (EP-86 T014):
 *   1. Pricing formula (Essentials) — ExplainerCard + 2-col grid (formula | PricingPreview)
 *   2. Payment methods (Advanced)   — per-property override toggle + method list
 * Also renders Late fee policy (Advanced, guarded by showLateFees) in the same
 * Advanced group as Payment methods.
 *
 * streamlinedMode=false (default / property detail Pricing tab): all sections
 *   rendered inline — functionally identical to the original layout.
 * streamlinedMode=true  (wizard Step 2): only the formula + preview are visible
 *   inline; Payment methods + Late fee are omitted.
 */
export function PropertyPricingFields({
  property,
  onSave,
  saveRef,
  onDirtyChange,
  prefillBanner,
  prefillPricingFormula,
  streamlinedMode = false,
  onFormulaChange,
  className,
}: PropertyPricingFieldsProps) {
  const { t } = useTranslation('properties')
  const { t: tc } = useTranslation('common')
  const { t: ts } = useTranslation('settings')
  // Step-2 (streamlinedMode) info banner copy lives in onboarding:wizard.* —
  // it's wizard-specific content, not a generic property-form string (EP-114 T010).
  const { t: to } = useTranslation('onboarding')

  const translateError = useApiErrorTranslator('properties')
  const addToast = useNotificationStore((s) => s.addToast)
  const updateProperty = useUpdateProperty()

  const { data: accountSettings } = useAccountSettings()
  const featureFlags = useFeatureFlags()

  const showLateFees =
    featureFlags.lateFees && (accountSettings?.settings?.late_fees_enabled ?? false)

  // ---------------------------------------------------------------------------
  // Local form state
  // ---------------------------------------------------------------------------

  const [pricingFormula, setPricingFormula] = useState<PricingFormula>(
    property.pricing_formula ?? [],
  )

  const [overridePayment, setOverridePayment] = useState<boolean>(
    property.payment_methods != null && Object.keys(property.payment_methods).length > 0,
  )

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodConfig>(
    property.payment_methods ?? {},
  )

  const [lateFeePolicy, setLateFeePolicy] = useState<LateFeePolicy>(
    property.late_fee_policy ?? DEFAULT_LATE_FEE_POLICY,
  )

  // ---------------------------------------------------------------------------
  // Prefill effect (EP-51): load prefillPricingFormula into state when provided
  // and the editor is still empty (no user edits yet). Never overwrites existing
  // entries — if the user has already edited the formula this is a no-op.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (prefillPricingFormula && prefillPricingFormula.length > 0 && pricingFormula.length === 0) {
      setPricingFormula(prefillPricingFormula)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillPricingFormula])

  // ---------------------------------------------------------------------------
  // Draft-mode formula change notification (T021)
  // Fires onFormulaChange whenever pricingFormula state changes, allowing the
  // import draft parent to track edits without triggering a real property save.
  // Skips the initial render by checking that onFormulaChange is provided.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    onFormulaChange?.(pricingFormula)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingFormula])

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSave = async () => {
    try {
      const saved = await updateProperty.mutateAsync({
        id: property.id,
        pricing_formula: pricingFormula,
        payment_methods: overridePayment ? paymentMethods : null,
        ...(showLateFees ? { late_fee_policy: lateFeePolicy } : {}),
      })
      onSave(saved)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  // Expose handleSave via saveRef so the wizard's Save & Continue button can
  // trigger this component's save without being inside the form DOM tree.
  useEffect(() => {
    if (saveRef) {
      saveRef.current = handleSave
    }
    return () => {
      if (saveRef) {
        saveRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRef, pricingFormula, overridePayment, paymentMethods, lateFeePolicy])

  // Compute dirty state by comparing current useState values to the property prop.
  // This is used by PropertyDetailPage to show per-tab dirty-dot badges and enable
  // the page-header Save button for the active tab.
  useEffect(() => {
    if (!onDirtyChange) return

    const originalFormula = JSON.stringify(property.pricing_formula ?? [])
    const currentFormula = JSON.stringify(pricingFormula)

    const originalOverride =
      property.payment_methods != null && Object.keys(property.payment_methods).length > 0
    const originalMethods = JSON.stringify(property.payment_methods ?? {})
    const currentMethods = JSON.stringify(paymentMethods)

    const originalPolicy = JSON.stringify(property.late_fee_policy ?? DEFAULT_LATE_FEE_POLICY)
    const currentPolicy = JSON.stringify(lateFeePolicy)

    const dirty =
      originalFormula !== currentFormula ||
      originalOverride !== overridePayment ||
      (overridePayment && originalMethods !== currentMethods) ||
      originalPolicy !== currentPolicy

    onDirtyChange(dirty)
  }, [onDirtyChange, property, pricingFormula, overridePayment, paymentMethods, lateFeePolicy])

  // ---------------------------------------------------------------------------
  // Section B — Payment methods
  // Factored as a JSX variable so the same markup is used both inline (normal
  // mode) and inside the OptionalSection wrapper (streamlined mode) without
  // duplicating any logic.
  // ---------------------------------------------------------------------------

  const paymentMethodsSection = (
    <section data-tour-target="payment-methods" aria-labelledby="payment-methods-heading">
      <Card title={t('sections.payment_methods')}>
        <h3 id="payment-methods-heading" className="sr-only">
          {t('sections.payment_methods')}
        </h3>
        <Toggle
          variant="primary"
          label={t('labels.payment_methods_override')}
          checked={overridePayment}
          onChange={(e) => {
            const checked = e.target.checked
            setOverridePayment(checked)
            if (checked && Object.keys(paymentMethods).length === 0) {
              const defaults = accountSettings?.settings?.payment_methods
              if (defaults) setPaymentMethods({ ...defaults })
            }
          }}
        />
        {overridePayment ? (
          <div className="mt-4 space-y-6">
            {/* ── Basic — bank transfer + cash ── */}
            <div>
              <h4 className="text-sm font-semibold text-base-content/50 uppercase tracking-wider mb-2">
                {ts('payment_methods.groups.basic')}
              </h4>
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Bank Transfer — min-w-0 prevents grid child from overflowing on narrow viewports */}
                <div className="border border-base-200 rounded-lg p-4 min-w-0">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{ts('payment_methods.bank_transfer.title')}</h4>
                      <p className="text-xs text-base-content/60">
                        {ts('payment_methods.bank_transfer.description')}
                      </p>
                    </div>
                    <Toggle
                      variant="primary"
                      size="sm"
                      checked={paymentMethods.bank_transfer?.enabled ?? false}
                      onChange={(e) =>
                        setPaymentMethods({
                          ...paymentMethods,
                          bank_transfer: {
                            ...paymentMethods.bank_transfer,
                            enabled: e.target.checked,
                          },
                        })
                      }
                    />
                  </div>
                  {paymentMethods.bank_transfer?.enabled && (
                    <div className="space-y-3 pt-3 border-t border-base-200">
                      <Input
                        label={ts('payment_methods.bank_transfer.bank_name')}
                        type="text"
                        placeholder={t('placeholders.bank_name_example')}
                        value={paymentMethods.bank_transfer?.bank_name ?? ''}
                        onChange={(e) =>
                          setPaymentMethods({
                            ...paymentMethods,
                            bank_transfer: {
                              ...paymentMethods.bank_transfer!,
                              bank_name: e.target.value,
                            },
                          })
                        }
                      />
                      <Input
                        label={ts('payment_methods.bank_transfer.account_number')}
                        type="text"
                        placeholder={t('placeholders.account_number_example')}
                        value={paymentMethods.bank_transfer?.account_number ?? ''}
                        onChange={(e) =>
                          setPaymentMethods({
                            ...paymentMethods,
                            bank_transfer: {
                              ...paymentMethods.bank_transfer!,
                              account_number: e.target.value,
                            },
                          })
                        }
                      />
                      <Input
                        label={ts('payment_methods.bank_transfer.account_name')}
                        type="text"
                        placeholder={t('placeholders.account_name_example')}
                        value={paymentMethods.bank_transfer?.account_name ?? ''}
                        onChange={(e) =>
                          setPaymentMethods({
                            ...paymentMethods,
                            bank_transfer: {
                              ...paymentMethods.bank_transfer!,
                              account_name: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>

                {/* Cash */}
                <div className="border border-base-200 rounded-lg p-4 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{ts('payment_methods.cash.title')}</h4>
                      <p className="text-xs text-base-content/60">
                        {ts('payment_methods.cash.description')}
                      </p>
                    </div>
                    <Toggle
                      variant="primary"
                      size="sm"
                      checked={paymentMethods.cash?.enabled ?? false}
                      onChange={(e) =>
                        setPaymentMethods({
                          ...paymentMethods,
                          cash: { ...paymentMethods.cash, enabled: e.target.checked },
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Stripe — PromptPay QR + credit card ── */}
            <div>
              <h4 className="text-sm font-semibold text-base-content/50 uppercase tracking-wider mb-2">
                {ts('payment_methods.groups.stripe')}
              </h4>
              <div className="grid gap-6 lg:grid-cols-2">
                {/* PromptPay */}
                <div className="border border-base-200 rounded-lg p-4 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{ts('payment_methods.promptpay.title')}</h4>
                      <p className="text-xs text-base-content/60">
                        {ts('payment_methods.promptpay.description')}
                      </p>
                    </div>
                    <Toggle
                      variant="primary"
                      size="sm"
                      checked={paymentMethods.promptpay?.enabled ?? false}
                      onChange={(e) =>
                        setPaymentMethods({
                          ...paymentMethods,
                          promptpay: { ...paymentMethods.promptpay, enabled: e.target.checked },
                        })
                      }
                    />
                  </div>
                </div>

                {/* Credit Card */}
                <div className="border border-base-200 rounded-lg p-4 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{ts('payment_methods.credit_card.title')}</h4>
                      <p className="text-xs text-base-content/60">
                        {ts('payment_methods.credit_card.description')}
                      </p>
                    </div>
                    <Toggle
                      variant="primary"
                      size="sm"
                      checked={paymentMethods.credit_card?.enabled ?? false}
                      onChange={(e) =>
                        setPaymentMethods({
                          ...paymentMethods,
                          credit_card: {
                            ...paymentMethods.credit_card,
                            enabled: e.target.checked,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Coming soon — disabled provider placeholders ── */}
            <div>
              <h4 className="text-sm font-semibold text-base-content/50 uppercase tracking-wider mb-2">
                {ts('payment_methods.groups.coming_soon')}
              </h4>
              <div className="grid gap-6 lg:grid-cols-2">
                {(['alipay', 'paysolution', 'beam'] as const).map((provider) => (
                  <div key={provider} className="border border-base-200 rounded-lg p-4 min-w-0 opacity-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">{ts(`payment_methods.${provider}.title`)}</h4>
                        <p className="text-xs text-base-content/60">
                          {ts(`payment_methods.${provider}.description`)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="ghost">{ts('payment_methods.coming_soon')}</Badge>
                        <Toggle
                          size="sm"
                          disabled
                          aria-label={ts(`payment_methods.${provider}.title`)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-base-content/60 mt-2">{t('labels.using_account_defaults')}</p>
        )}
      </Card>
    </section>
  )

  // ---------------------------------------------------------------------------
  // Section C — Late fee policy (guarded by showLateFees)
  // ---------------------------------------------------------------------------

  const lateFeeSection = showLateFees ? (
    <section data-tour-target="late-fee" aria-labelledby="late-fee-heading">
      <ExplainerCard
        id="late-fee-explainer"
        icon="⚠️"
        title={t('late_fee_policy.title')}
        body={t('explainer.late_fee.body')}
        className="mb-4"
      >
        <p>{t('explainer.late_fee.details')}</p>
      </ExplainerCard>

      <Card>
        <h3 id="late-fee-heading" className="sr-only">
          {t('late_fee_policy.title')}
        </h3>
        {/* Enable toggle */}
        <div className="mb-4">
          <Toggle
            variant="primary"
            label={t('late_fee_policy.enable_label')}
            checked={lateFeePolicy.enabled}
            onChange={(e) =>
              setLateFeePolicy({ ...lateFeePolicy, enabled: e.target.checked })
            }
          />
        </div>

        {/* Disable warning banner */}
        {!lateFeePolicy.enabled && property.late_fee_policy?.enabled && (
          <Alert variant="warning" icon={null} role="note" className="mb-4 text-sm">
            <span>{t('late_fee_policy.disable_warning')}</span>
          </Alert>
        )}

        {/* Policy fields */}
        <div
          className={`space-y-4 ${!lateFeePolicy.enabled ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <div className="grid gap-4 md:grid-cols-2">
            {/* Fee Type — min-w-0 prevents grid children from overflowing on narrow viewports */}
            <div className="min-w-0">
              <Select
                label={t('late_fee_policy.fee_type_label')}
                value={lateFeePolicy.type}
                onChange={(e) =>
                  setLateFeePolicy({
                    ...lateFeePolicy,
                    type: e.target.value as 'fixed' | 'percentage',
                  })
                }
                disabled={!lateFeePolicy.enabled}
                options={[
                  { value: 'fixed', label: t('late_fee_policy.fee_type_fixed') },
                  { value: 'percentage', label: t('late_fee_policy.fee_type_percentage') },
                ]}
                className="w-auto"
              />
            </div>

            {/* Amount */}
            <div className="form-control min-w-0">
              <Input
                label={t('late_fee_policy.amount_label')}
                type="number"
                min={0}
                step={lateFeePolicy.type === 'percentage' ? '0.01' : '1'}
                value={lateFeePolicy.amount}
                onChange={(e) =>
                  setLateFeePolicy({
                    ...lateFeePolicy,
                    amount: Number(e.target.value),
                  })
                }
                disabled={!lateFeePolicy.enabled}
                leftIcon={lateFeePolicy.type === 'fixed' ? <span className="text-base-content/60">฿</span> : undefined}
                rightAddon={lateFeePolicy.type === 'percentage' ? <span className="text-base-content/60">%</span> : undefined}
                hint={lateFeePolicy.type === 'percentage' ? t('late_fee_policy.amount_hint_percentage') : undefined}
              />
            </div>
          </div>

          {/* Grace Period */}
          <Input
            label={t('late_fee_policy.grace_period_label')}
            type="number"
            className="w-full md:w-48"
            min={0}
            step={1}
            value={lateFeePolicy.grace_period_days}
            onChange={(e) =>
              setLateFeePolicy({
                ...lateFeePolicy,
                grace_period_days: Math.max(0, Math.floor(Number(e.target.value))),
              })
            }
            disabled={!lateFeePolicy.enabled}
            hint={t('late_fee_policy.grace_period_hint')}
          />
        </div>
      </Card>
    </section>
  ) : null

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={`space-y-6 ${className ?? ''}`}>
      {/* ------------------------------------------------------------------ */}
      {/* Section 1 — Pricing formula (Essentials)                            */}
      {/* Always rendered inline in both normal and streamlined modes.        */}
      {/* ------------------------------------------------------------------ */}
      <section data-tour-target="pricing-formula" aria-labelledby="pricing-formula-heading">
        {/* streamlinedMode (wizard step 2): a non-collapsible info banner
            (EP-114 T010) replaces the collapsible ExplainerCard — its own
            new onboarding:wizard.step_2_formula_banner_* keys, NOT the
            step-2 explainer-body key retired alongside it.
            Non-streamlined (standalone Pricing tab): the original
            collapsible ExplainerCard, unchanged. */}
        {streamlinedMode ? (
          <ExplainerCard
            id="step-2-formula-banner"
            variant="info"
            icon={<Lightbulb className="w-4 h-4 text-primary-content" aria-hidden="true" />}
            title={to('wizard.step_2_formula_banner_title')}
            body={to('wizard.step_2_formula_banner_body')}
            // Desktop only (owner, 2026-08-05). On a phone this banner is five
            // lines of explanation sitting between the user and the fields they
            // came to edit, on a screen that is already short — and the same
            // ground is covered by the live bill preview a tap away. Hidden, not
            // deleted: it still earns its place at `md+` where it costs nothing.
            className="mb-4 hidden md:block"
          />
        ) : (
          <ExplainerCard
            id="pricing-explainer"
            icon="💰"
            title={t('sections.utility_rates')}
            body={t('explainer.pricing.body')}
            className="mb-4"
          >
            <p>{t('explainer.pricing.details')}</p>
          </ExplainerCard>
        )}

        {/* Apply-from-file banner (EP-51 wizard prefill) */}
        {prefillBanner}

        {/* 2-col layout on md+: formula editor left, PricingPreview right.
            EP-114 T010 — Figma specs a fixed 700px/448px split with a 24px
            gap (a 25:16 ratio). We express it as a FLUID `25fr 16fr` ratio
            rather than literal px columns: the wizard caps this component's
            container at max-w-[1172px] (OnboardingWizardPage step-2 case),
            and 25:16 of (1172 − 24px gap) resolves to EXACTLY 700px / 448px
            there — while the standalone /properties/:id Pricing tab and
            /properties/new (no width cap) get the same visual proportion at
            whatever width they render, instead of overflowing or clipping a
            literal 1172px-wide grid (T010 chose the fluid-ratio option over
            centering a fixed-width grid on the standalone pages).
            min-w-0 on both grid children prevents auto min-width from blowing out the column
            on narrow viewports (Tailwind grid items default to min-width: auto). */}
        <div className="grid gap-6 md:grid-cols-[25fr_16fr]">
          <Card className="md:col-span-1 min-w-0">
            {/* In streamlined mode the collapsible ExplainerCard is replaced by
                the info banner above (not hidden outright), so we still surface
                a visible section heading here. In normal mode it stays sr-only
                since the ExplainerCard above already provides the visual label. */}
            <h3
              id="pricing-formula-heading"
              className={streamlinedMode ? 'font-semibold mb-3' : 'sr-only'}
            >
              {t(streamlinedMode ? 'sections.pricing_formula' : 'sections.utility_rates')}
            </h3>
            <p className="text-sm text-base-content/60 mb-4">
              {t('sections.utility_rates_description')}
            </p>
            <PricingFormulaEditor value={pricingFormula} onChange={setPricingFormula} />
          </Card>

          {/* PricingPreview: desktop = in-flow card; mobile = sticky bottom bar (built-in) */}
          <div data-tour-target="pricing-preview" className="md:col-span-1 min-w-0">
            <PricingPreview formula={pricingFormula} />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Sections 2 + 3 — Payment methods + Late fee (Advanced)             */}
      {/*                                                                     */}
      {/* streamlinedMode=true  → collapsed OptionalSection (wizard Step 2)  */}
      {/* streamlinedMode=false → rendered inline (Pricing tab, unchanged)   */}
      {/* ------------------------------------------------------------------ */}
      {/* Advanced (payment methods / late fee) — standalone Pricing tab
          only. The onboarding wizard (streamlinedMode / step 2) omits them
          entirely; they're configured later from the property Pricing tab, and
          the save uses the state defaults (no payment override). */}
      {!streamlinedMode && (
        <>
          {paymentMethodsSection}
          {lateFeeSection}
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Save button — hidden when parent supplies saveRef (wizard handles save) */}
      {/* ------------------------------------------------------------------ */}
      {!saveRef && (
        <div className="flex justify-end gap-3">
          <Button onClick={handleSave} loading={updateProperty.isPending}>
            {tc('actions.save_changes')}
          </Button>
        </div>
      )}
    </div>
  )
}
